import React from "react";

import { useTranslation } from "@refinedev/core";

import CloseRounded from "@mui/icons-material/CloseRounded";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";

import { AlertEmailPreference, getAlertPrefOptions } from "@/hooks/useAlertEmailPreference";

export type CoverageAction = "add" | "remove";

export interface CoveragePractitionerOption {
  id: string;
  name: string;
}

/** Default translation prefix (org-admin practitioner list strings). */
export const DEFAULT_COVERAGE_PREFIX = "pages.practitioner.coverage";

/**
 * Blue field label matching the app's shrunk InputLabel style, with an optional
 * red required asterisk (secondary.main) like the rest of the forms.
 */
export const FieldLabel = ({ text, required = false }: { text: string; required?: boolean }) => (
  <Typography variant="caption" color="primary">
    {text}
    {required && (
      <Typography component="span" color="secondary.main">
        {" *"}
      </Typography>
    )}
  </Typography>
);

export interface EmailPreferenceFieldProps {
  /** Blue field label shown above the dropdown (e.g. "Email Preferences"). */
  label: string;
  /** Optional paragraph rendered above the label (description / instruction). */
  description?: React.ReactNode;
  value: AlertEmailPreference;
  onChange: (preference: AlertEmailPreference) => void;
  disabled?: boolean;
}

/** Alert email-preference dropdown with its label and optional description. */
export const EmailPreferenceField = ({ label, description, value, onChange, disabled }: EmailPreferenceFieldProps) => {
  const { translate } = useTranslation();
  const options = getAlertPrefOptions(translate);

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value as AlertEmailPreference);
  };

  return (
    <Stack spacing={1}>
      {description && (
        <Typography variant="body2" color="text.primary">
          {description}
        </Typography>
      )}
      <Stack spacing={0.5}>
        <FieldLabel text={label} />
        <Select
          fullWidth
          size="small"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          data-testid="coverage-email-pref-select"
        >
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
};

export interface CoverageDialogShellProps {
  open: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmColor?: "primary" | "error";
  confirmDisabled?: boolean;
  isSaving?: boolean;
  children: React.ReactNode;
}

/** Shared dialog chrome for the coverage add/remove dialogs. */
export const CoverageDialogShell = ({
  open,
  title,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmColor = "primary",
  confirmDisabled = false,
  isSaving = false,
  children,
}: CoverageDialogShellProps) => {
  const { translate } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="coverage-confirm-dialog-title"
      aria-describedby="coverage-confirm-dialog-description"
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" id="coverage-confirm-dialog-title">
            {title}
          </Typography>
          <Typography component="span" color="text.primary">
            <IconButton
              onClick={onCancel}
              data-testid="coverage-confirm-dialog-close"
              aria-label={translate("buttons.cancel")}
            >
              <CloseRounded />
            </IconButton>
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} pt={1} id="coverage-confirm-dialog-description">
          {children}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} justifyContent="flex-end" width="100%">
          <Button
            variant="dialog"
            color="inherit"
            data-testid="coverage-confirm-dialog-cancel"
            onClick={onCancel}
            disabled={isSaving}
          >
            {translate("buttons.cancel")}
          </Button>
          <Button
            variant="dialog"
            color={confirmColor}
            data-testid="coverage-confirm-dialog-confirm"
            onClick={onConfirm}
            disabled={confirmDisabled || isSaving}
            autoFocus
          >
            {confirmLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
